// Snapshot → CanonicalSet: the extension's poller snapshot on the engine's
// comparison surface.
//
// Audio lanes are structurally CanonicalTracks already (AudioClipInfo is a
// superset of CanonicalClip) and are passed through by reference. MIDI lanes
// need a real projection: a MidiClipInfo has no audio fields, and its wire
// geometry is the FLATTENED form (markers 0..duration, no loop) that its
// `notes` were computed for. The result is a fresh wrapper object — nothing
// downstream (compile, digests) relies on its identity; the snapshot whose
// identity IS load-bearing is the raw one the scope/stamper share
// (live-scope.ts), which this never touches.

import type {
  AudioTrackInfo,
  MidiClipInfo,
  MidiTrackInfo,
  SessionAudioClipInfo,
  SessionMidiClipInfo,
  Snapshot,
} from "../seed-history.js";
import type { CanonicalClip, CanonicalSet, CanonicalTrack } from "./canonical.js";

export function snapshotAsCanonicalSet(s: Snapshot): CanonicalSet {
  const midi = s.midiTracks ?? [];
  return {
    song: s.song,
    audioTracks: s.audioTracks.map(audioLaneOf),
    ...(midi.length > 0 ? { midiTracks: midi.map(midiLaneOf) } : {}),
  };
}

/**
 * An audio lane on the engine's surface. A lane with no Session View clips
 * is the SAME object (AudioClipInfo is a superset of CanonicalClip); one
 * with session clips gets a fresh wrapper whose clip list is the arrangement
 * clips followed by the session clips, each carrying its `slot`.
 */
export function audioLaneOf(t: AudioTrackInfo): CanonicalTrack {
  const session = t.sessionClips ?? [];
  if (session.length === 0) return t;
  return {
    name: t.name,
    mute: t.mute,
    solo: t.solo,
    arm: t.arm,
    arrangementClips: [...t.arrangementClips, ...session.map(sessionAudioClipOf)],
  };
}

/** A Session View audio clip as the engine compares and ships it. */
export function sessionAudioClipOf(c: SessionAudioClipInfo): CanonicalClip {
  return {
    name: c.name,
    startTime: 0,
    duration: c.duration,
    startMarker: c.startMarker,
    endMarker: c.endMarker,
    looping: c.looping,
    loopStart: c.loopStart,
    loopEnd: c.loopEnd,
    color: c.color,
    muted: c.muted,
    filePath: c.filePath,
    warping: c.warping,
    warpMode: c.warpMode,
    slot: c.slot,
  };
}

export function midiLaneOf(t: MidiTrackInfo): CanonicalTrack {
  const session = (t.sessionClips ?? []).map(sessionMidiClipOf);
  return {
    name: t.name,
    mute: t.mute,
    solo: t.solo,
    arm: t.arm,
    arrangementClips: [...t.arrangementClips.map(midiClipOf), ...session],
    trackKind: "midi",
  };
}

/** A Session View MIDI clip: the flattened form, at startTime 0, with its slot. */
export function sessionMidiClipOf(c: SessionMidiClipInfo): CanonicalClip {
  return { ...midiClipOf(c), startTime: 0, slot: c.slot, slotLooping: c.looping };
}

/** A MIDI clip as the engine compares and ships it. Over-cap clips
 * (`notesOmitted`) carry no `notes`, which the compiler reads as "nothing
 * complete to propose". */
export function midiClipOf(c: MidiClipInfo): CanonicalClip {
  const out: CanonicalClip = {
    name: c.name,
    startTime: c.startTime,
    duration: c.duration,
    startMarker: 0,
    endMarker: c.duration,
    looping: false,
    loopStart: 0,
    loopEnd: c.duration,
    color: c.color,
    muted: c.muted,
    filePath: "",
    warping: false,
    warpMode: { value: 0 },
    clipKind: "midi",
    notesDigest: c.notesDigest,
  };
  if (!c.notesOmitted) out.notes = c.notes;
  return out;
}
